import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { pushForm } from '../utils/formStack';

// ── Toast ───────────────────────────────────────────────────────────────────
const ToastCtx = createContext(null);
export const useToast = () => useContext(ToastCtx);
export function ToastProvider({ children }) {
  const [msg, setMsg] = useState(null);
  const show = useCallback((m) => {
    setMsg(m);
    setTimeout(() => setMsg(null), 2600);
  }, []);
  return (
    <ToastCtx.Provider value={show}>
      {children}
      {msg && <div className="toast">{msg}</div>}
    </ToastCtx.Provider>
  );
}

// ── Modal ───────────────────────────────────────────────────────────────────
// Registers with the global formStack so any back gesture (Android hardware
// back, Escape, Backspace) closes the top-most modal first and returns to
// whatever is underneath. `onClose` is that close action.
// `wide` → ~1040px (multi-column forms); `dense` → tighter fields so the form fits one screen.
export function Modal({ title, onClose, children, footer, className = '', fullScreen, wide, dense }) {
  // Keep the latest callbacks in a ref so the once-registered handler always
  // sees current values without re-registering each render.
  const cb = useRef({});
  cb.current = { onClose };
  useEffect(() => {
    const unregister = pushForm({
      close: () => { cb.current.onClose && cb.current.onClose(); },
    });
    return unregister;
  }, []);
  return (
    <div className={`modal-back${fullScreen ? ' full' : ''}`} onClick={onClose}>
      <div className={`modal${fullScreen ? ' full' : ''}${wide ? ' wide' : ''}${dense ? ' dense' : ''}${className ? ` ${className}` : ''}`} onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <h3>{title}</h3>
          <button className="x" onClick={onClose}>&times;</button>
        </div>
        <div className="modal-body">{children}</div>
        {footer && <div className="modal-foot">{footer}</div>}
      </div>
    </div>
  );
}

// Time picker ---------------------------------------------------------------
// One box you can type into ("9", "930", "9 pm", "21:30") plus a clock icon
// that opens the dial. AM/PM is NOT repeated next to the box - the dial owns
// it - so a typed hour without a suffix keeps the meridiem it already had, or
// falls back to the sensible one for an event (mornings 8-11, else evening).
// Value in / out is 24-hour "HH:MM" - what the database stores; '' when unset.

// Reads what a human typed into 24-hour "HH:MM", or '' when it cannot be read.
//   "9" -> 09:00 · "930" / "9:30" -> 09:30 · "19" -> 19:00 · "7p" -> 19:00
// `fallbackPm` decides AM/PM for a 1-12 hour that carries no suffix.
export const parseTimeInput = (text, fallbackPm = false) => {
  const raw = String(text || '').trim().toLowerCase();
  if (!raw) return '';
  const pmTyped = /p/.test(raw);
  const amTyped = /a/.test(raw);
  const digits = raw.replace(/[^0-9]/g, '').slice(0, 4);
  if (!digits) return '';
  let h;
  let m = 0;
  if (digits.length <= 2) {
    h = Number(digits);
  } else {
    h = Number(digits.slice(0, digits.length - 2));
    m = Number(digits.slice(-2));
  }
  if (h > 23 || m > 59) return '';
  // 13-23 is already 24-hour; 1-12 follows the typed suffix, else the fallback.
  if (h <= 12) {
    const pm = amTyped ? false : (pmTyped || fallbackPm);
    if (pm && h < 12) h += 12;
    if (!pm && h === 12) h = 0;
  }
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

// 24-hour "HH:MM" -> what the user reads and edits: "9:00 AM", "7:30 PM".
const displayTime = (v) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(v || ''));
  if (!m) return '';
  const h = Number(m[1]);
  return `${h % 12 === 0 ? 12 : h % 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};
const isPm = (v) => {
  const m = /^(\d{1,2}):/.exec(String(v || ''));
  return !!m && Number(m[1]) >= 12;
};
// Nobody holds a function at 3 in the morning: 8-11 read as AM, the rest PM.
const guessPm = (text) => {
  const h = Number(String(text || '').replace(/[^0-9]/g, '').slice(0, 2));
  return !(h >= 8 && h <= 11);
};

// The dial itself: tap the hour, then the minute (the same two-step dial the
// SmartPagar app uses). `value` / `onOk` speak the same 24-hour "HH:MM".
const FACE = 224;            // clock face diameter
const RAD = 92;              // ring radius the numbers and the hand sit on
const HOURS_FACE = [12, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
const MINS_FACE = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

function ClockDialog({ label, value, onOk, onClose }) {
  const parsed = /^(\d{1,2}):(\d{2})$/.exec(String(value || '')) || null;
  const [h24, setH24] = React.useState(() => (parsed ? Number(parsed[1]) : 18));
  const [min, setMin] = React.useState(() => (parsed ? Number(parsed[2]) : 0));
  const [mode, setMode] = React.useState('hour');
  const faceRef = React.useRef(null);

  const pm = h24 >= 12;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  const setMeridiem = (nextPm) => setH24(nextPm ? (h24 % 12) + 12 : h24 % 12);

  // Where on the dial did the finger land? 0 deg = 12 o-clock, clockwise.
  const angleAt = (e) => {
    const box = faceRef.current.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - (box.left + box.width / 2);
    const dy = point.clientY - (box.top + box.height / 2);
    return (Math.atan2(dy, dx) * 180 / Math.PI + 90 + 360) % 360;
  };
  const pick = (e) => {
    const angle = angleAt(e);
    if (mode === 'hour') {
      const picked = Math.round(angle / 30) % 12 || 12;      // 1..12
      setH24(pm ? (picked === 12 ? 12 : picked + 12) : (picked === 12 ? 0 : picked));
      setMode('minute');                                     // straight on to minutes
    } else {
      setMin(((Math.round(angle / 6) % 60) + 60) % 60);
    }
  };

  const handAngle = mode === 'hour' ? (h12 % 12) * 30 : min * 6;
  const nums = mode === 'hour' ? HOURS_FACE : MINS_FACE;
  const isOn = (n) => (mode === 'hour' ? n === h12 : n === min);

  return ReactDOM.createPortal(
    <div className="clock-back" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="clock-card" role="dialog" aria-label={label + ' - pick a time'}>
        <div className="clock-head">
          <div className="clock-digits">
            <button type="button" className={'clock-num' + (mode === 'hour' ? ' on' : '')} onClick={() => setMode('hour')}>
              {String(h12).padStart(2, '0')}
            </button>
            <span>:</span>
            <button type="button" className={'clock-num' + (mode === 'minute' ? ' on' : '')} onClick={() => setMode('minute')}>
              {String(min).padStart(2, '0')}
            </button>
          </div>
          <div className="clock-ap">
            <button type="button" className={'time-ap' + (pm ? '' : ' on')} onClick={() => setMeridiem(false)}>AM</button>
            <button type="button" className={'time-ap' + (pm ? ' on' : '')} onClick={() => setMeridiem(true)}>PM</button>
          </div>
        </div>

        <div ref={faceRef} className="clock-face" style={{ width: FACE, height: FACE }}
          onMouseDown={pick} onTouchStart={pick} onTouchMove={pick}>
          <div className="clock-centre" />
          <div className="clock-hand" style={{ height: RAD - 6, transform: `translateX(-50%) rotate(${handAngle}deg)` }} />
          <div className="clock-tip" style={{
            top: `calc(50% - ${RAD * Math.cos(handAngle * Math.PI / 180)}px - 16px)`,
            left: `calc(50% + ${RAD * Math.sin(handAngle * Math.PI / 180)}px - 16px)`,
          }} />
          {nums.map((n, i) => {
            const a = (i * 30 - 90) * Math.PI / 180;
            return (
              <div key={n} className={'clock-n' + (isOn(n) ? ' on' : '')} style={{
                left: FACE / 2 - 14 + RAD * Math.cos(a),
                top: FACE / 2 - 14 + RAD * Math.sin(a),
              }}>{mode === 'minute' ? String(n).padStart(2, '0') : n}</div>
            );
          })}
        </div>

        <div className="clock-foot">
          <button type="button" className="btn light" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary"
            onClick={() => onOk(String(h24).padStart(2, '0') + ':' + String(min).padStart(2, '0'))}>OK</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export function TimeField({ label, value, onChange, required }) {
  // While the box has focus we hold exactly what was typed and touch nothing:
  // re-formatting on every keystroke was what made editing feel jumpy.
  const [typed, setTyped] = React.useState(null);
  const [clockOpen, setClockOpen] = React.useState(false);

  const commit = () => {
    if (typed === null) return;
    const text = typed.trim();
    setTyped(null);
    if (text === '') { onChange(''); return; }          // cleared on purpose
    const parsed = parseTimeInput(text, value ? isPm(value) : guessPm(text));
    if (parsed) onChange(parsed);                        // unreadable -> keep the old value
  };

  return (
    <label className="field">
      {label}{required && ' *'}
      <div className="time-field">
        <input type="text" value={typed !== null ? typed : displayTime(value)} placeholder="9:00 PM"
          aria-label={label + ' - type a time, or use the clock'}
          title="Type 9, 930, 9 am or 21:30 - or tap the clock"
          onChange={(e) => setTyped(e.target.value)}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
            else if (e.key === 'Escape') setTyped(null);
          }}
          onBlur={commit} />
        <button type="button" className="time-clock" title="Pick on a clock"
          aria-label={label + ' - pick on a clock'} onClick={() => setClockOpen(true)}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="9" /><polyline points="12 7 12 12 16 14" />
          </svg>
        </button>
      </div>
      {clockOpen && (
        <ClockDialog label={label}
          value={typed !== null ? parseTimeInput(typed, guessPm(typed)) || value : value}
          onClose={() => setClockOpen(false)}
          onOk={(v) => { setClockOpen(false); setTyped(null); onChange(v); }} />
      )}
    </label>
  );
}

// ── Field ───────────────────────────────────────────────────────────────────
export function Field({ label, type = 'text', value, onChange, options, required, placeholder, rows }) {
  // Every time input in the app goes through the typed box + clock dial above.
  if (type === 'time') return <TimeField label={label} value={value} onChange={onChange} required={required} />;
  return (
    <label className="field">
      {label}{required && ' *'}
      {type === 'select' ? (
        <select value={value ?? ''} onChange={(e) => onChange(e.target.value)}>
          <option value="">- select -</option>
          {(options || []).map(o => (
            <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
          ))}
        </select>
      ) : type === 'textarea' ? (
        <textarea rows={rows || 3} value={value ?? ''} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input type={type} value={value ?? ''} placeholder={placeholder}
          inputMode={type === 'number' ? 'decimal' : undefined}
          onChange={(e) => onChange(e.target.value)} />
      )}
    </label>
  );
}

export function Spinner({ label = 'Loading...' }) {
  return <div className="empty">{label}</div>;
}
